import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, Plus, Copy, Check, Users, Image as ImageIcon, X } from "lucide-react";
import axios from "axios";
import { AppHeader } from "@/components/AppHeader";
import { MemoryCard } from "@/components/MemoryCard";
import { AddMemoryModal } from "@/components/AddMemoryModal";
import { ActivityFeed } from "@/components/ActivityFeed";
import { useFamily } from "@/lib/useFamily";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function TimelineFeed({ memories }) {
    const groups = {};
    memories.forEach((m) => {
        const d = m.created_at ? new Date(m.created_at) : new Date();
        const key = d.toLocaleDateString(undefined, { month: "long", year: "numeric" });
        (groups[key] = groups[key] || []).push(m);
    });
    return (
        <div data-testid="memory-timeline" className="relative pl-8">
            <div className="absolute left-2 top-0 h-full w-0.5 bg-warmgray" />
            {Object.entries(groups).map(([label, items]) => (
                <div key={label} className="mb-10">
                    <div className="relative mb-5">
                        <span className="absolute -left-[27px] top-1 block h-3 w-3 rotate-45 bg-gradient-to-br from-lavender to-peach" />
                        <p className="font-mono text-xs font-semibold uppercase tracking-[0.25em] text-[#C98A94]">{label}</p>
                    </div>
                    <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                        {items.map((m) => (
                            <MemoryCard key={m.memory_id} memory={m} />
                        ))}
                    </div>
                </div>
            ))}
        </div>
    );
}

export default function Dashboard() {
    const { checking, user, family } = useFamily();
    const [memories, setMemories] = useState([]);
    const [milestones, setMilestones] = useState([]);
    const [activity, setActivity] = useState([]);
    const [modalOpen, setModalOpen] = useState(false);
    const [copied, setCopied] = useState(false);
    const [view, setView] = useState("grid");
    const [searchParams, setSearchParams] = useSearchParams();

    const authorFilter = searchParams.get("author") || "";
    const taggedFilter = searchParams.get("tagged") || "";

    const loadFeed = useCallback(async () => {
        const params = new URLSearchParams();
        if (authorFilter) params.set("author_id", authorFilter);
        if (taggedFilter) params.set("tagged_id", taggedFilter);
        const qs = params.toString();
        const { data } = await axios.get(`${API}/memories/feed${qs ? `?${qs}` : ""}`, { withCredentials: true });
        setMemories(data.memories);
    }, [authorFilter, taggedFilter]);

    const loadMilestones = useCallback(async () => {
        const { data } = await axios.get(`${API}/milestones`, { withCredentials: true });
        setMilestones(data.milestones);
    }, []);

    const loadActivity = useCallback(async () => {
        const { data } = await axios.get(`${API}/families/activity`, { withCredentials: true });
        setActivity(data.activity);
    }, []);

    useEffect(() => {
        if (checking) return;
        loadFeed();
        loadMilestones();
        loadActivity();
    }, [checking, loadFeed, loadMilestones, loadActivity]);

    const copyCode = async () => {
        try {
            await navigator.clipboard.writeText(family.code);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch {}
    };

    if (checking) {
        return (
            <main data-testid="dashboard-loading" className="flex min-h-screen items-center justify-center bg-cream">
                <Loader2 size={32} className="animate-spin text-lavender" />
            </main>
        );
    }

    const filterId = authorFilter || taggedFilter;
    const filterMember = filterId ? family.members.find((m) => m.user_id === filterId) : null;
    const filterLabel = authorFilter
        ? `Memories by ${filterMember?.name || filterMember?.email || "this member"}`
        : taggedFilter
          ? `Memories tagged with ${filterMember?.name || filterMember?.email || "this member"}`
          : "";

    return (
        <main data-testid="dashboard-page" className="min-h-screen bg-cream text-navy">
            <AppHeader user={user} />

            <div className="mx-auto max-w-6xl px-6 py-10">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, ease: [0.76, 0, 0.24, 1] }}
                    className="flex flex-wrap items-end justify-between gap-4"
                >
                    <div>
                        <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.3em] text-[#C98A94]">Your family archive</p>
                        <h1 data-testid="dashboard-family-name" className="mt-2 font-display text-3xl tracking-tight md:text-4xl">{family.name}</h1>
                        <div className="mt-2 flex flex-wrap items-center gap-4 text-sm text-navy">
                            <button
                                data-testid="family-code-copy"
                                onClick={copyCode}
                                className="flex items-center gap-2 rounded-full border-2 border-warmgray bg-ivory px-4 py-1.5 font-mono text-xs font-semibold tracking-widest transition-colors duration-300 hover:border-peach"
                            >
                                {copied ? <Check size={13} /> : <Copy size={13} />} CODE: {family.code}
                            </button>
                            <Link to="/family" data-testid="family-members-link" className="flex items-center gap-1.5 text-sm text-[#2C3347] underline-offset-4 hover:underline">
                                <Users size={14} /> {family.members.length} {family.members.length === 1 ? "member" : "members"}
                            </Link>
                        </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-3">
                        <div className="flex rounded-full border-2 border-warmgray p-1">
                            {[
                                { id: "grid", label: "Grid" },
                                { id: "timeline", label: "Timeline" },
                            ].map((v) => (
                                <button
                                    key={v.id}
                                    data-testid={`view-${v.id}`}
                                    onClick={() => setView(v.id)}
                                    className={`rounded-full px-5 py-2 text-xs font-semibold transition-colors duration-300 ${
                                        view === v.id ? "bg-navy text-white" : "text-navy hover:bg-ivory"
                                    }`}
                                >
                                    {v.label}
                                </button>
                            ))}
                        </div>
                        <button
                            data-testid="add-memory-button"
                            onClick={() => setModalOpen(true)}
                            className="btn-gradient flex items-center gap-2 rounded-full px-7 py-4 text-sm font-semibold text-white transition-transform duration-300 hover:-translate-y-0.5"
                        >
                            <Plus size={18} /> Add a Memory
                        </button>
                    </div>
                </motion.div>

                <ActivityFeed items={activity} />

                {filterLabel && (
                    <div data-testid="feed-filter-chip" className="mt-6 flex items-center gap-3 rounded-2xl border-2 border-peach bg-ivory px-5 py-3">
                        <p className="flex-1 text-sm font-medium text-navy">{filterLabel}</p>
                        <button
                            data-testid="feed-filter-clear"
                            onClick={() => setSearchParams({})}
                            aria-label="Clear filter"
                            className="flex items-center gap-1 rounded-full border-2 border-warmgray px-3 py-1 text-xs font-semibold text-navy transition-colors duration-300 hover:border-peach"
                        >
                            <X size={12} /> Clear
                        </button>
                    </div>
                )}

                {memories.length === 0 ? (
                    <div data-testid="feed-empty" className="mt-14 rounded-3xl border-2 border-dashed border-warmgray bg-ivory p-14 text-center">
                        <ImageIcon size={32} className="mx-auto text-lavender" />
                        <h2 className="mt-4 font-display text-2xl">
                            {filterLabel ? "Nothing here yet." : "Your archive is waiting for its first story."}
                        </h2>
                        <p className="mx-auto mt-2 max-w-md text-sm leading-relaxed text-navy">
                            {filterLabel
                                ? "No memories match this filter yet."
                                : "Add a photo, a video, a voice story, or a written memory — everything becomes part of your family's living timeline."}
                        </p>
                        {!filterLabel && (
                            <button
                                data-testid="add-memory-button-empty"
                                onClick={() => setModalOpen(true)}
                                className="btn-gradient mt-6 inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-sm font-semibold text-white transition-transform duration-300 hover:-translate-y-0.5"
                            >
                                <Plus size={16} /> Add your first memory
                            </button>
                        )}
                    </div>
                ) : (
                    <div data-testid="memory-feed" className="mt-10">
                        {view === "grid" ? (
                            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                                {memories.map((m) => (
                                    <MemoryCard key={m.memory_id} memory={m} />
                                ))}
                            </div>
                        ) : (
                            <TimelineFeed memories={memories} />
                        )}
                    </div>
                )}
            </div>

            <AddMemoryModal
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                onSaved={loadFeed}
                milestones={milestones}
                members={family.members}
            />
        </main>
    );
}
